import {
  ageShort,
  draftHiddenFor,
  fmtDate,
  isAssignee,
  REMINDER_LABEL,
  reviewsWaitingFor,
  type ReviewWait,
  type BoardItem,
  type FeatureRequestView,
  type ReminderView,
  type RemindersPayload,
  type RequestsPayload,
  type Viewer,
} from "@boem/core/shared";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api.ts";
import { remindersApi } from "../reminders-api.ts";
import { JumpContext, Linkify } from "../util.tsx";
import { ErrorLine, Modal, SecretHint, useSecretCheck } from "./Modal.tsx";
import { PrChip } from "./GitHub.tsx";
import { stalePrsFor, type StalePr } from "../prs.ts";
import { compareDue, dueBadge, isDueThisWeek, isOverdue } from "@boem/core/due";
import { BoardDayContext } from "./Due.tsx";
import "../foryou.css";

const why = (e: unknown) => (e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");

function BellIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5v2.7L2.2 11.2a.6.6 0 0 0 .5.9h10.6a.6.6 0 0 0 .5-.9l-1.3-2.5V6A4.5 4.5 0 0 0 8 1.5ZM6.2 13.2a1.9 1.9 0 0 0 3.6 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The "For you" bell on the header line, for everyone. ("Request a feature" is in the avatar menu: see LogView.) */
export function MastExtras({ items, viewer }: { items: BoardItem[]; viewer?: Viewer }) {
  const [data, setData] = useState<RemindersPayload | null>(null);
  const [open, setOpen] = useState<"foryou" | null>(null);
  const load = useCallback(() => remindersApi.list().then(setData, () => {}), []);

  // refresh with the board (a reminder run pings every open board), on focus, and every few minutes
  useEffect(() => {
    load();
  }, [load, items]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 5 * 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [load]);

  // PRs waiting for your review, and stale PRs on items you have or that you opened (docs/github.md):
  // counted with the unread reminders
  // someone else's draft (OEM-286) is never in your For you
  const visible = useMemo(() => (viewer ? items.filter((it) => !draftHiddenFor(it, viewer.id)) : items), [items, viewer]);
  const reviews = useMemo(() => (viewer ? reviewsWaitingFor(visible, viewer.id) : []), [visible, viewer]);
  const stale = useMemo(() => {
    const mine = new Set(reviews.map((r) => r.pr.id));
    return viewer ? stalePrsFor(visible, viewer).filter((s) => !mine.has(s.pr.id)) : [];
  }, [visible, viewer, reviews]);
  // due dates (OEM-267): my open items (assigned to me, or claimed) that are overdue, and those due this week
  const { today } = useContext(BoardDayContext);
  const due = useMemo(() => (viewer ? dueForMe(items, viewer.id, today) : { overdue: [], week: [] }), [items, viewer, today]);
  const n = (data?.unread ?? 0) + reviews.length + stale.length + due.overdue.length;
  return (
    <>
      <button
        className="btn fy-bell"
        data-new={n ? "1" : undefined}
        aria-label={n ? `For you: ${n} new reminder${n === 1 ? "" : "s"}` : "For you: no new reminders"}
        title="For you"
        onClick={() => setOpen("foryou")}
      >
        <BellIcon />
        <span className="fy-bell-label">For you</span>
        {n > 0 && <span className="fy-count">{n > 99 ? "99+" : n}</span>}
      </button>
      {open === "foryou" && <ForYouModal data={data} reviews={reviews} stale={stale} due={due} reload={load} onClose={() => setOpen(null)} />}
    </>
  );
}

/**
 * My open items with a due date (OEM-267): assigned to me, alone or with others (OEM-285; claiming one assigns it to me too), overdue
 * (the most overdue first) and due this week (the soonest first). Projects are left out. Pure.
 */
export function dueForMe(items: BoardItem[], viewerId: string, today: string): { overdue: BoardItem[]; week: BoardItem[] } {
  const mine = items.filter((it) => it.status === "open" && !it.archived && it.kind !== "project" && isAssignee(it, viewerId) && !draftHiddenFor(it, viewerId) && it.due);
  const byDue = (a: BoardItem, b: BoardItem) => compareDue(a.due, b.due) || a.num - b.num;
  return { overdue: mine.filter((it) => isOverdue(it, today)).sort(byDue), week: mine.filter((it) => isDueThisWeek(it, today)).sort(byDue) };
}

/** For you's "Overdue" and "Due this week": each item with its badge and a way to open it. */
export function DueList({ title, items, today, tone, onOpen }: { title: string; items: BoardItem[]; today: string; tone?: "overdue"; onOpen: (num: number) => void }) {
  if (!items.length) return null;
  return (
    <section className="fy-stale fy-due" aria-label={title}>
      <div className="fy-kind" data-tone={tone}>
        {title} &middot; {items.length}
      </div>
      <ul className="fy-list">
        {items.map((it) => {
          const b = dueBadge(it, today);
          return (
            <li key={it.num} className="fy-item unread">
              <div className="fy-meta">
                {b && (
                  <span className="role due-badge" data-tone={b.tone} title={b.title}>
                    {b.text}
                  </span>
                )}
              </div>
              <div className="fy-text">
                {it.key?.label ?? `#${it.num}`} {it.title}
              </div>
              <div className="fy-acts">
                <button className="note-btn" onClick={() => onOpen(it.num)}>
                  Open {it.key?.label ?? `#${it.num}`}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const TEAMS_LINE: Record<RemindersPayload["teams"], string> = {
  off: "Reminders show here. Teams messages come later, with the Teams rollout.",
  "not-installed": "To get these as a Teams chat message too, add the Board app for yourself in Teams (Apps, search “OEM UAP Board”, Add).",
  on: "These are also sent to you as a Teams chat message.",
};

/** "PRs waiting for your review": each item in PR review that waits for you, with how long its PR has waited. */
export function ReviewList({ reviews, onOpen, now }: { reviews: ReviewWait<BoardItem>[]; onOpen: (num: number) => void; now?: Date }) {
  if (!reviews.length) return null;
  return (
    <section className="fy-stale fy-reviews" aria-label="Pull requests waiting for your review">
      <div className="fy-kind">PRs waiting for your review &middot; {reviews.length}</div>
      <ul className="fy-list">
        {reviews.map(({ item, pr }) => (
          <li key={`${item.num}:${pr.id}`} className="fy-item unread">
            <div className="fy-meta">
              <PrChip pr={pr} now={now} />
              <span className="fy-when">waiting {ageShort(pr.readyAt ?? pr.openedAt, now)}</span>
              {pr.reviewStale && <span className="fy-when fy-stale-tag">stale review</span>}
            </div>
            <div className="fy-text">
              {item.key?.label ?? `#${item.num}`} {item.title}: {pr.title} <span className="fy-when">by {pr.author}</span>
            </div>
            <div className="fy-acts">
              <a className="note-btn" href={pr.url} target="_blank" rel="noopener noreferrer">
                Review on GitHub
              </a>
              <button className="note-btn" onClick={() => onOpen(item.num)}>
                Open {item.key?.label ?? `#${item.num}`}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ForYouModal({
  data,
  reviews = [],
  stale = [],
  due = { overdue: [], week: [] },
  reload,
  onClose,
}: {
  data: RemindersPayload | null;
  reviews?: ReviewWait<BoardItem>[];
  stale?: StalePr[];
  due?: { overdue: BoardItem[]; week: BoardItem[] };
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const { nums, jump } = useContext(JumpContext);
  const { today } = useContext(BoardDayContext);
  const [error, setError] = useState<string | null>(null);
  const act = async (p: Promise<unknown>) => {
    setError(null);
    try {
      await p;
      await reload();
    } catch (e) {
      setError(why(e));
    }
  };
  // "#12" links inside a reminder close the panel before jumping
  const go = (n: number) => {
    onClose();
    jump(n);
  };
  const openItem = (r: ReminderView) => {
    if (!r.read) remindersApi.read(r.id).then(reload, () => {});
    if (r.itemNum) go(r.itemNum);
  };

  const list = data?.reminders ?? [];
  const current = list.filter((r) => r.current);
  const done = list.filter((r) => !r.current);
  const row = (r: ReminderView) => (
    <li key={r.id} className={`fy-item${r.read ? "" : " unread"}${r.current ? "" : " done"}`}>
      <div className="fy-meta">
        <span className="fy-kind">{REMINDER_LABEL[r.kind]}</span>
        <span className="fy-when">{fmtDate(r.at)}</span>
        {r.teams && <span className="fy-when">also in Teams</span>}
        {!r.current && <span className="fy-when">no longer current</span>}
      </div>
      <div className="fy-text">
        <Linkify text={r.text} />
      </div>
      <div className="fy-acts">
        {r.itemNum && (
          <button className="note-btn" onClick={() => openItem(r)}>
            Open #{r.itemNum}
          </button>
        )}
        {!r.read && r.current && (
          <button className="note-btn" onClick={() => act(remindersApi.read(r.id))}>
            Mark read
          </button>
        )}
        <button className="note-btn" onClick={() => act(remindersApi.dismiss(r.id))}>
          Dismiss
        </button>
      </div>
    </li>
  );

  return (
    <Modal onClose={onClose} wide>
      <JumpContext.Provider value={{ nums, jump: go }}>
        <h3>For you</h3>
        <p>Items waiting on you, questions for you, high-priority items nobody has, and the weekly summary for leads. New ones arrive on weekday mornings.</p>
        <DueList title="Overdue" tone="overdue" items={due.overdue} today={today} onOpen={go} />
        <DueList title="Due this week" items={due.week} today={today} onOpen={go} />
        <ReviewList reviews={reviews} onOpen={go} />
        {stale.length > 0 && (
          <section className="fy-stale" aria-label="Stale pull requests">
            <div className="fy-kind">Stale pull requests &middot; {stale.length}</div>
            <ul className="fy-list">
              {stale.map(({ item, pr }) => (
                <li key={pr.id} className="fy-item unread">
                  <div className="fy-meta">
                    <PrChip pr={pr} />
                    <span className="fy-when">no activity since {fmtDate(pr.lastActivityAt)}</span>
                  </div>
                  <div className="fy-text">
                    {item.key?.label ?? `#${item.num}`} {item.title}: {pr.title}
                  </div>
                  <div className="fy-acts">
                    <button className="note-btn" onClick={() => go(item.num)}>
                      Open #{item.num}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        {!data ? (
          <p className="count-line">Loading&hellip;</p>
        ) : current.length ? (
          <ul className="fy-list">{current.map(row)}</ul>
        ) : (
          <div className="fy-empty">Nothing waiting on you.</div>
        )}
        {done.length > 0 && (
          <details className="fy-done">
            <summary>Handled since &middot; {done.length}</summary>
            <ul className="fy-list">{done.map(row)}</ul>
          </details>
        )}
        {data && (
          <label className="fy-pause">
            <input type="checkbox" checked={data.paused} onChange={(e) => act(remindersApi.pause(e.target.checked))} />
            Pause my reminders{data.paused ? " (paused: nothing new until you turn this off)" : ""}
          </label>
        )}
        <ErrorLine error={error} />
        <div className="row">
          {current.some((r) => !r.read) && (
            <button className="btn" onClick={() => act(remindersApi.readAll())}>
              Mark all read
            </button>
          )}
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
        {data && <div className="hint">{TEAMS_LINE[data.teams]}</div>}
      </JumpContext.Provider>
    </Modal>
  );
}

const STATUS_LABEL: Record<FeatureRequestView["status"], string> = { created: "filed", failed: "not filed", pending: "filing" };
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** "Request a feature": a GitHub issue for Claude to build (members who can ask it to). */
export function RequestModal({ items, onClose, toast }: { items: BoardItem[]; onClose: () => void; toast: (msg: string) => void }) {
  const [info, setInfo] = useState<RequestsPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [refs, setRefs] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => remindersApi.requests().then(setInfo, (e) => setLoadError(why(e))), []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const byNum = useMemo(() => new Map(items.map((i) => [i.num, i])), [items]);
  const linked = useMemo(() => [...new Set((refs.match(/\d{1,5}/g) ?? []).map(Number))].slice(0, 20), [refs]);
  const unknown = linked.filter((n) => !byNum.has(n));
  const secrets = useSecretCheck(title, body);
  const configured = !!info?.github.configured;
  const ready = configured && !busy && title.trim().length >= 5 && body.trim().length >= 10 && !secrets.length && !unknown.length;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await remindersApi.request({ title: title.trim(), body: body.trim(), items: linked });
      toast(r.issueNumber ? `Filed as GitHub issue #${r.issueNumber}.` : "Request filed.");
      setTitle("");
      setBody("");
      setRefs("");
    } catch (e) {
      setError(why(e));
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <Modal onClose={onClose} wide>
      <h3>Request a feature</h3>
      <p>
        Describe what you&rsquo;d like built. It&rsquo;s filed as a GitHub issue labelled <code>build-request</code>
        {info?.github.repo ? ` in ${info.github.repo}` : ""}, and Claude picks it up from there.
      </p>
      {info && !configured && (
        <div className="fy-warn" role="note">
          <b>GitHub isn&rsquo;t connected on this server yet,</b> so requests can&rsquo;t be filed. Whoever runs the server needs to set:
          <ul>
            {info.github.missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          The steps are in <code>docs/reminders-and-requests.md</code>.
        </div>
      )}
      <label>Title</label>
      <input type="text" value={title} maxLength={200} placeholder="e.g. Assign every item in the current filter at once" onChange={(e) => setTitle(e.target.value)} />
      <label style={{ marginTop: 12 }}>What should it do?</label>
      <textarea
        rows={6}
        value={body}
        placeholder="Who needs it, what they'd do, and what should happen. Reference board items with #12."
        onChange={(e) => setBody(e.target.value)}
      />
      <SecretHint findings={secrets} />
      <label style={{ marginTop: 12 }}>Related board items (optional)</label>
      <input type="text" value={refs} placeholder="e.g. #84, #91" onChange={(e) => setRefs(e.target.value)} />
      {linked.length > 0 && (
        <div className="fy-refs">
          {linked.map((n) => {
            const it = byNum.get(n);
            return it ? (
              <span className="fy-ref" key={n} title={it.title}>
                #{n} {clip(it.title, 48)}
              </span>
            ) : (
              <span className="fy-ref bad" key={n}>
                #{n} isn&rsquo;t on the board
              </span>
            );
          })}
        </div>
      )}
      <ErrorLine error={error ?? loadError} />
      <div className="row">
        <button className="btn" onClick={onClose}>
          Close
        </button>
        <button className="btn primary" disabled={!ready} onClick={submit}>
          {busy ? "Filing…" : "File the request"}
        </button>
      </div>
      {info && info.requests.length > 0 && (
        <>
          <label style={{ marginTop: 18 }}>Your requests</label>
          <ul className="fy-reqs">
            {info.requests.map((r) => (
              <li key={r.id}>
                <span className={`fy-status s-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                <b>{r.title}</b> <span className="fy-when">{fmtDate(r.at)}</span>
                {r.issueUrl && /^https:\/\//.test(r.issueUrl) && (
                  <>
                    {" "}
                    &middot;{" "}
                    <a href={r.issueUrl} target="_blank" rel="noopener noreferrer">
                      GitHub #{r.issueNumber}
                    </a>
                  </>
                )}
                {r.items.length > 0 && <span className="fy-when"> &middot; {r.items.map((n) => `#${n}`).join(", ")}</span>}
                {r.error && <div className="fy-err">{r.error}</div>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
